import * as yup from 'yup';

const abnRegex = /^\d{11}$/;

const addressSchema = yup.object({
  street: yup.string().max(200),
  city: yup.string().max(100),
  state: yup.string().max(50),
  postcode: yup.string().max(10),
  country: yup.string().max(100),
});

const bankDetailsSchema = yup.object({
  bankName: yup.string().max(100),
  bsb: yup
    .string()
    .transform((value) => (value ? value.replace(/\s/g, '') : ''))
    .matches(/^\d{3}-?\d{3}$/, { message: 'BSB must be 6 digits (e.g., 123-456)', excludeEmptyString: true }),
  accountNumber: yup.string().max(20),
  accountName: yup.string().max(100),
});

export const issuerSchema = yup.object({
  name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100),
  businessName: yup.string().max(200),
  abn: yup
    .string()
    .nullable()
    .transform((value) => (value ? value.replace(/\s/g, '') : value))
    .test('abn-format', 'ABN must be 11 digits', (value) => !value || abnRegex.test(value)),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email'),
  phone: yup.string().max(20),
  address: addressSchema.nullable(),
  bankDetails: bankDetailsSchema.nullable(),
  paymentTerms: yup.string().max(200),
});

export const organisationSchema = yup.object({
  name: yup
    .string()
    .required('Organisation name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(200),
  abn: yup
    .string()
    .nullable()
    .transform((value) => (value ? value.replace(/\s/g, '') : value))
    .test('abn-format', 'ABN must be 11 digits', (value) => !value || abnRegex.test(value)),
  email: yup.string().email('Please enter a valid email'),
  phone: yup.string().max(20),
  address: addressSchema.nullable(),
  contactPerson: yup.string().max(100),
});

const lineItemSchema = yup.object({
  description: yup.string().required('Description is required').max(500),
  quantity: yup
    .number()
    .required('Quantity is required')
    .positive('Quantity must be positive')
    .typeError('Quantity must be a number'),
  unitPrice: yup
    .number()
    .required('Unit price is required')
    .min(0, 'Unit price cannot be negative')
    .typeError('Unit price must be a number'),
});

export const invoiceSchema = yup.object({
  invoiceNumber: yup
    .string()
    .required('Invoice number is required')
    .max(50),
  issuerId: yup.string().required('Please select a billing profile'),
  organisationId: yup.string().required('Please select an organisation'),
  issueDate: yup.string().required('Issue date is required'),
  dueDate: yup
    .string()
    .required('Due date is required')
    .test('after-issue', 'Due date must be on or after issue date', function (value) {
      const { issueDate } = this.parent;
      if (!issueDate || !value) return true;
      return new Date(value) >= new Date(issueDate);
    }),
  lineItems: yup
    .array()
    .of(lineItemSchema)
    .min(1, 'At least one line item is required')
    .required(),
  gstIncluded: yup.boolean(),
  notes: yup.string().max(2000),
  templateId: yup
    .string()
    .oneOf(['classic', 'modern', 'detailed'])
    .default('classic'),
});
